// src/api/fetchWithAuth.js
import { useProjectStore } from "../stores/useProjectStore";
import { usePlatformOrgContext } from "../stores/usePlatformOrgContext";
import { useOrgStore } from "../stores/useOrgStore";
import { useAuthStore } from "../stores/useAuthStore";
 
// -----------------------------------------------------------------------------
// PROJECT_AWARE_PREFIXES
// Any endpoint whose backend handler scopes data by project must be listed here.
// It receives the x-aiprime-project header.
// Platform routes (/api/v1/platform/*) are intentionally excluded — they are
// org-level and must NOT receive a project header.
// -----------------------------------------------------------------------------
const PROJECT_AWARE_PREFIXES = [
  // Core resource routes (original)
  "/api/v1/aois",
  "/api/v1/imagery",
  "/api/v1/layers",
  "/api/v1/projects",
 
  // Post-migration additions
  "/api/v1/operating-aois",
  "/api/v1/datasets",
  "/api/v1/geometry",
  "/api/v1/wells",
  "/api/v1/admin/projects",   // admin project management is still project-scoped
 
  // Dynamic project-bootstrap route — matched by prefix since it's /:projectId/...
  "/api/v1/",                 // intentionally broad to catch /api/v1/${projectId}/map-bootstrap
                              // and any other dynamic project-id routes under v1
 
  // Event and AI routes
  "/api/ai/runs",
  "/api/events",
 
  // Site identity routes
  "/siteid/sites",
];
 
// Matches API_BASE-prefixed site routes at runtime (API_BASE may be a full origin).
// We resolve this separately since API_BASE is an env var, not a fixed prefix.
function isSiteRoute(url) {
  return url.includes("/sites/") && (
    url.includes("/features") ||
    url.includes("/viewport") ||
    /\/sites\/[^/]+$/.test(url)
  );
}
 
function isProjectAware(url) {
  // Exclude platform routes explicitly — they share the /api/v1/ prefix
  // but must never receive project context.
  if (url.startsWith("/api/v1/platform")) return false;
  if (url.startsWith("/api/v1/org")) return false;
  if (url.startsWith("/api/v1/orgs")) return false;
  if (url.startsWith("/api/v1/invites")) return false;
  if (url.startsWith("/api/v1/org-invites")) return false;
  // THE PORTAL IS NOT PROJECT-SCOPED. A plan review has no project -- the same
  // reason layerStaging's projectId became scopeId -- and PROJECT_AWARE_PREFIXES
  // carries "/api/v1/" deliberately broad, so without this line every portal
  // call sends x-aiprime-project: <activeProjectId || "global">.
  //
  // It is INERT on the server: projectContext never refuses, it sets req.project
  // and logs. So this changes no behaviour. What it stops is every portal
  // request LOGGING a plan review uuid beside a project key that means nothing
  // here -- REM-308's shape in a header rather than a column.
  //
  // WITH THE TRAILING SLASH, matching ALLOW_PREFIX_PORTAL exactly. The
  // "/api/v1/org" guard above has none and therefore also catches /api/v1/orgs
  // and anything else beginning "org"; this one should not inherit that.
  if (url.startsWith("/api/v1/portal/")) return false;
 
  if (PROJECT_AWARE_PREFIXES.some(p => url.startsWith(p))) return true;
  if (isSiteRoute(url)) return true;
 
  return false;
}
 
// -----------------------------------------------------------------------------
// Single token-refresh coordinator
//
// Solution: one shared in-flight refresh promise. All callers — fetchWithAuth
// and ensureFreshToken — wait on the same promise and always read kc.token
// after it resolves, never a stale store snapshot.
// -----------------------------------------------------------------------------
let refreshPromise = null;
 
export async function getKcFreshToken() {
  const kc = window.__keycloak;
  if (!kc || !kc.authenticated) throw new Error("Not authenticated");
 
  // If a refresh is already in flight, wait for it instead of firing another
  if (refreshPromise) return refreshPromise;
 
  const exp = (() => {
    try {
      return JSON.parse(atob(kc.token.split(".")[1])).exp || 0;
    } catch {
      return 0;
    }
  })();
 
  const now = Math.floor(Date.now() / 1000);
 
  // Token is fresh enough — no refresh needed
  if (exp - now > 60) return kc.token;
 
  // Kick off exactly one refresh and share it with all concurrent callers
  refreshPromise = kc.updateToken(120)
    .then(() => {
      // Sync the store so ensureFreshToken readers stay consistent
      useAuthStore.getState().setAuth({
        token: kc.token,
        user: useAuthStore.getState().user,
        roles: useAuthStore.getState().roles,
        orgId: useAuthStore.getState().orgId,
      });
      return kc.token;
    })
    .finally(() => {
      refreshPromise = null;
    });
 
  return refreshPromise;
}
 
// -----------------------------------------------------------------------------
// Effective org id = impersonated (platform admin) || the user's
// regular active org. Exported so non-fetch consumers (SSE URL
// builder in collabApi.js, etc.) can derive the same value without
// duplicating the platformOrgId || appOrgId logic.
export function computeEffectiveOrgId() {
  const { activeOrgId: appOrgId } = useOrgStore.getState();
  const { activeOrgId: platformOrgId } = usePlatformOrgContext.getState();
  return platformOrgId || appOrgId || null;
}

// fetchWithAuth
// -----------------------------------------------------------------------------
export async function fetchWithAuth(url, options = {}) {
  const kc = window.__keycloak;
  if (!kc || !kc.authenticated) throw new Error("Not authenticated");

  // Single coordinated refresh — safe to call concurrently
  const token = await getKcFreshToken();

  const { activeProjectId } = useProjectStore.getState();

  const headers = {
    ...(options.headers || {}),
    Authorization: `Bearer ${token}`,
  };

  const effectiveOrgId = computeEffectiveOrgId();
  if (effectiveOrgId) {
    headers["X-Active-Org"] = effectiveOrgId;
  }
 
  if (isProjectAware(url)) {
    headers["x-aiprime-project"] = activeProjectId || "global";
  }
 
  return fetch(url, {
    ...options,
    headers,
  });
}